import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ReviewCard from "../components/ReviewCard";
import StarRating from "../components/StarRating";
import LoadingSpinner from "../components/LoadingSpinner";
import apiClient from "../api/apiClient";
import { TbArrowBackUp } from "react-icons/tb";

const MoreReviews = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [overallRating, setOverallRating] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const observerTarget = useRef(null);
  const mockupId = new URLSearchParams(window.location.search).get("id");

  const fetchReviews = useCallback(async (pageNum) => {
    if (!mockupId) return;

    try {
      const result = await apiClient.get(
        `/reviews/?listing_id=${mockupId}&page=${pageNum}&page_size=10`
      );

      if (result.ok) {
        const newReviews = result.data.results || [];
        
        if (pageNum === 1) {
          setReviews(newReviews);
        } else {
          setReviews((prev) => [...prev, ...newReviews]);
        }

        // Check if there are more pages
        setHasMore(result.data.next !== null);
        
        // Set overall rating if available
        if (result.data.overall_rating !== undefined) {
          setOverallRating(result.data.overall_rating);
        }
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoading(false);
    }
  }, [mockupId]);

  useEffect(() => {
    if (mockupId) {
      fetchReviews(1);
    }
  }, [mockupId, fetchReviews]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading]);

  // Fetch next page when page changes
  useEffect(() => {
    if (page > 1 && hasMore && !loading) {
      fetchReviews(page);
    }
  }, [page, hasMore, loading, fetchReviews]);

  if (loading && reviews.length === 0) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <LoadingSpinner color="#FF3E1F" />
      </div>
    );
  }

  return (
    <div className="container py-5 my-md-5 my-3" style={{ marginBottom: "100px" }}>
      <TbArrowBackUp
        style={{ fontSize: "32px", cursor: "pointer" }}
        className="back-arrow-new shadow me-2 mb-3 rounded-5 p-1 arrow-button"
        onClick={() => navigate(-1)}
      />
      
      <h2 className="mb-4">All Reviews</h2>

      {/* Overall Rating */}
      {overallRating !== undefined && overallRating !== null && (
        <div className="mb-4 pb-3" style={{ borderBottom: "2px solid #e0e0e0" }}>
          <div className="d-flex align-items-center gap-2">
            <StarRating rating={overallRating} readOnly={true} size="large" />
            <span className="ms-2" style={{ fontSize: "20px", fontWeight: "600" }}>
              {Number(overallRating)?.toFixed(1)} out of 5
            </span>
            <span className="ms-2 text-muted">
              ({reviews.length} {reviews.length === 1 ? "review" : "reviews"})
            </span>
          </div>
        </div>
      )}

      {/* Reviews List */}
      <div className="reviews-list">
        {reviews.length === 0 ? (
          <p className="text-muted">No reviews found.</p>
        ) : (
          reviews.map((review, index) => (
            <ReviewCard key={review?.id || index} review={review} />
          ))
        )}
      </div>

      {/* Loading indicator for infinite scroll */}
      {loading && reviews.length > 0 && (
        <div className="d-flex justify-content-center mt-4">
          <LoadingSpinner color="#FF3E1F" />
        </div>
      )}

      {/* Observer target for infinite scroll */}
      <div ref={observerTarget} style={{ height: "20px" }} />

      {!hasMore && reviews.length > 0 && (
        <p className="text-center text-muted mt-4">
          No more reviews to load
        </p>
      )}
    </div>
  );
};

export default MoreReviews;

